
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../state/auth';
import { chatService } from '../domain/services/chatService';
import { ChatMessage } from '../domain/types';
import { ROUTES } from '../domain/routes';

export const FloatingSupport = () => {
    const navigate = useNavigate(); // Add this
    const { user } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputText, setInputText] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [hasUnread, setHasUnread] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    // Initial Load
    useEffect(() => {
        if (!user) return;
        const unsubscribe = chatService.startPollingMessages(user.id, (data) => {
            setMessages(data);
        });
        return () => unsubscribe();
    }, [user]);

    // Auto-scroll to bottom
    useEffect(() => {
        if (isOpen && scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            setHasUnread(false);
        }
    }, [messages, isOpen]);

    const handleSend = async (overrideText?: string) => {
        const text = overrideText || inputText;
        if (!text.trim() || !user) return;
        
        if (!overrideText) setInputText('');
        
        // API Call - Message will appear via subscription
        await chatService.sendMessage(user.id, text);
        
        // Simulate Bot Response logic in background
        setIsTyping(true);
        await chatService.simulateBotResponse(user.id, text);
        setIsTyping(false);
        
        if (!isOpen) setHasUnread(true);
    };

    const handleButtonClick = async (value: string) => {
        if (value.startsWith('nav_')) {
            const target = value.replace('nav_', '');
            
            // Explicit Close & Clear Action
            if (target === 'close') {
                setIsOpen(false);
                if (user) await chatService.clearHistory(user.id);
                return;
            }

    if (target === 'history') navigate(ROUTES.ORDERS_HISTORY);
    else if (target === 'club') navigate(ROUTES.CLUB);
    else if (target === 'catalog') navigate(ROUTES.CATALOG);
    else if (target === 'profile') navigate(ROUTES.PROFILE);
    else if (target === 'addresses') navigate(ROUTES.ADDRESSES);
    else if (target === 'wallet_history') navigate(ROUTES.WALLET_HISTORY);
    else if (target === 'topup') navigate(ROUTES.WALLET_TOPUP);
    else if (target.startsWith('prod_')) navigate(`/product/${target.replace('prod_', '')}`);
            
            setIsOpen(false);
        } else {
            // Trigger a new bot flow by "sending" the value as text
            handleSend(value);
        }
    };

    const handleAutoStart = async () => {
        setIsOpen(true);
        if (messages.length === 0 && user) {
            // Disparamos la respuesta del bot directamente sin que el usuario "hable"
            setIsTyping(true);
            await chatService.simulateBotResponse(user.id, 'start_flow');
            setIsTyping(false);
        }
    };

    if (!user) return null; // Only for logged users

    return (
        <>
            {/* Toggle Button */}
            <div className={`absolute bottom-24 right-4 z-[60] transition-transform duration-300 ${isOpen ? 'translate-y-2 opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'}`}>
                <button 
                    onClick={handleAutoStart}
                    className="w-14 h-14 bg-gradient-to-tr from-primary to-orange-400 rounded-full shadow-lg shadow-orange-500/30 flex items-center justify-center text-white relative hover:scale-110 transition-transform active:scale-95"
                >
                    <span className="material-symbols-outlined text-3xl">chat_bubble</span>
                    {hasUnread && <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 rounded-full border-2 border-white animate-pulse"></span>}
                </button>
            </div>

            {/* Chat Window */}
            <div className={`absolute bottom-24 right-4 w-[calc(100%-32px)] md:w-80 bg-surface-light dark:bg-[#1c1917] rounded-3xl shadow-2xl border border-black/5 dark:border-white/10 z-[60] overflow-hidden flex flex-col transition-all duration-300 origin-bottom-right ${isOpen ? 'scale-100 opacity-100 h-[500px]' : 'scale-75 opacity-0 h-0 pointer-events-none'}`}>
                {/* Header */}
                <div className="bg-gradient-to-r from-primary to-orange-600 p-4 flex justify-between items-center text-white shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                            <span className="material-symbols-outlined">support_agent</span>
                        </div>
                        <div>
                            <h3 className="font-bold text-sm">Soporte AllTuc</h3>
                            <p className="text-[10px] opacity-90 flex items-center gap-1">
                                <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span> En línea
                            </p>
                        </div>
                    </div>
                    <button onClick={() => setIsOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Messages Area */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-black/20 no-scrollbar">
                    <div className="text-center my-2">
                        <span className="text-[10px] bg-black/5 dark:bg-white/5 px-2 py-1 rounded-full text-text-secondary dark:text-gray-400 font-bold uppercase tracking-widest">
                            Asistente Virtual
                        </span>
                    </div>
                    
                    {messages.map((msg) => (
                        <div key={msg.id} className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`}>
                            <div className={`max-w-[85%] rounded-2xl p-3 text-sm leading-relaxed shadow-sm ${msg.sender === 'user' ? 'bg-primary text-white rounded-tr-none' : 'bg-white dark:bg-white/10 text-text-main dark:text-white rounded-tl-none border border-black/5 dark:border-white/5'}`}>
                                <p>{msg.text}</p>
                                <div className={`text-[9px] mt-1 text-right opacity-70 ${msg.sender === 'user' ? 'text-white' : 'text-gray-400'}`}>
                                    {new Date(msg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </div>
                            </div>
                            
                            {/* Render Buttons if present */}
                            {msg.buttons && (
                                <div className="flex flex-wrap gap-2 mt-2 max-w-[90%] animate-fade-in">
                                    {msg.buttons.map((btn, i) => (
                                        <button 
                                            key={i} 
                                            onClick={() => handleButtonClick(btn.value)}
                                            className="bg-primary/10 hover:bg-primary text-primary hover:text-white border border-primary/20 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider transition-all active:scale-95 shadow-sm"
                                        >
                                            {btn.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                    
                    {isTyping && (
                        <div className="flex justify-start">
                            <div className="bg-white dark:bg-white/10 rounded-2xl rounded-tl-none p-3 shadow-sm border border-black/5 dark:border-white/5 flex gap-1 items-center h-10">
                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></span>
                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce delay-75"></span>
                                <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce delay-150"></span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Input Area */}
                <div className="p-3 bg-surface-light dark:bg-surface-dark border-t border-black/5 dark:border-white/5 shrink-0">
                    <form 
                        onSubmit={(e) => { e.preventDefault(); handleSend(); }}
                        className="flex gap-2 items-center bg-gray-100 dark:bg-black/20 rounded-full px-2 py-1 border border-transparent focus-within:border-primary/50 focus-within:bg-white dark:focus-within:bg-black/40 transition-all"
                    >
                        <input 
                            type="text" 
                            placeholder="Escribe un mensaje..." 
                            className="flex-1 bg-transparent border-none text-sm px-3 py-2 text-text-main dark:text-white placeholder-gray-400 focus:ring-0 outline-none"
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                        />
                        <button 
                            type="submit"
                            disabled={!inputText.trim()}
                            className="w-9 h-9 bg-primary text-white rounded-full flex items-center justify-center shadow-md disabled:opacity-50 disabled:shadow-none hover:scale-105 active:scale-95 transition-all"
                        >
                            <span className="material-symbols-outlined text-[18px]">send</span>
                        </button>
                    </form>
                </div>
            </div>
        </>
    );
};
